/**
 * TaskCard — tarjeta interactiva para validar una tarea (Fase 52).
 *
 * Acciones: ▶ Escuchar 10s, ✅ Confirmar, ✏️ Editar, 🗑 Eliminar.
 * Estados: default (sin acción), editando (inputs inline para text,
 * responsibleName, deadline + toggle requiresNotification), confirmada
 * (borde verde), descartada (gris, tachado). Las ediciones persisten en
 * structureTaskEditsAtom hasta confirm.
 */

import { useState, useCallback } from "react";
import styled from "styled-components";
import type { Task, StructureDoc } from "../../lib/types.ts";
import type { TaskEdit } from "../../state/atoms.ts";

// ---------------------------------------------------------------------------
// Styled
// ---------------------------------------------------------------------------

const Card = styled.div<{ $confirmed: boolean; $dismissed: boolean }>`
  background: ${({ theme }) => theme.color.surface};
  border: 1px solid
    ${({ theme, $confirmed }) =>
      $confirmed ? theme.color.success : theme.color.border};
  border-radius: ${({ theme }) => theme.radius.md};
  padding: ${({ theme }) => theme.space.md};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.space.sm};
  opacity: ${({ $dismissed }) => ($dismissed ? 0.5 : 1)};
`;

const TopRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${({ theme }) => theme.space.sm};
`;

const PlayButton = styled.button`
  flex-shrink: 0;
  background: none;
  border: 1px solid ${({ theme }) => theme.color.border};
  border-radius: ${({ theme }) => theme.radius.sm};
  color: ${({ theme }) => theme.color.accent};
  cursor: pointer;
  font-size: ${({ theme }) => theme.font.size.sm};
  padding: 2px 6px;
  line-height: 1;

  &:hover {
    background: ${({ theme }) => theme.color.accentMuted};
  }
`;

const Timestamp = styled.span`
  font-family: ${({ theme }) => theme.font.mono};
  font-size: ${({ theme }) => theme.font.size.sm};
  color: ${({ theme }) => theme.color.textMuted};
  white-space: nowrap;
  min-width: 55px;
`;

const Content = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.space.xs};
  min-width: 0;
`;

const TaskText = styled.p<{ $dismissed: boolean }>`
  margin: 0;
  font-size: ${({ theme }) => theme.font.size.md};
  color: ${({ theme }) => theme.color.textPrimary};
  text-decoration: ${({ $dismissed }) =>
    $dismissed ? "line-through" : "none"};
  line-height: 1.4;
`;

const InlineInput = styled.input`
  font-size: ${({ theme }) => theme.font.size.md};
  padding: ${({ theme }) => theme.space.xs} ${({ theme }) => theme.space.sm};
  border: 1px solid ${({ theme }) => theme.color.accent};
  border-radius: ${({ theme }) => theme.radius.sm};
  background: ${({ theme }) => theme.color.surface};
  color: ${({ theme }) => theme.color.textPrimary};
  width: 100%;
  box-sizing: border-box;
`;

const SmallInput = styled.input`
  font-size: ${({ theme }) => theme.font.size.sm};
  padding: 2px 6px;
  border: 1px solid ${({ theme }) => theme.color.accent};
  border-radius: ${({ theme }) => theme.radius.sm};
  background: ${({ theme }) => theme.color.surface};
  color: ${({ theme }) => theme.color.textPrimary};
  min-width: 0;
`;

const EditFields = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.space.sm};
  margin-top: ${({ theme }) => theme.space.xs};
`;

const EditField = styled.label`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.space.xs};
  font-size: ${({ theme }) => theme.font.size.xs};
  color: ${({ theme }) => theme.color.textMuted};
`;

const ToggleButton = styled.button<{ $active: boolean }>`
  font-size: ${({ theme }) => theme.font.size.xs};
  padding: 2px 8px;
  border: 1px solid
    ${({ theme, $active }) =>
      $active ? theme.color.warning : theme.color.border};
  border-radius: ${({ theme }) => theme.radius.sm};
  background: ${({ theme, $active }) =>
    $active ? theme.color.warningMuted : "none"};
  color: ${({ theme, $active }) =>
    $active ? theme.color.warning : theme.color.textMuted};
  cursor: pointer;
  white-space: nowrap;

  &:hover {
    border-color: ${({ theme }) => theme.color.warning};
  }
`;

const MetaRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.space.sm} ${({ theme }) => theme.space.md};
  font-size: ${({ theme }) => theme.font.size.xs};
  color: ${({ theme }) => theme.color.textMuted};
`;

const MetaItem = styled.span`
  white-space: nowrap;
`;

const MetaLabel = styled.span`
  font-weight: ${({ theme }) => theme.font.weight.medium};
`;

const NotificationBadge = styled.span`
  font-size: ${({ theme }) => theme.font.size.xs};
  padding: 1px 6px;
  border-radius: ${({ theme }) => theme.radius.sm};
  background: ${({ theme }) => theme.color.warningMuted};
  color: ${({ theme }) => theme.color.warning};
  white-space: nowrap;
`;

const StatusBadge = styled.span<{ $kind: "confirm" | "dismiss" }>`
  font-size: ${({ theme }) => theme.font.size.xs};
  padding: 1px 6px;
  border-radius: ${({ theme }) => theme.radius.sm};
  background: ${({ theme, $kind }) =>
    $kind === "confirm"
      ? theme.color.successMuted
      : theme.color.errorMuted};
  color: ${({ theme, $kind }) =>
    $kind === "confirm" ? theme.color.success : theme.color.error};
  font-weight: ${({ theme }) => theme.font.weight.medium};
`;

const Actions = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.space.xs};
  flex-wrap: wrap;
`;

const ActionButton = styled.button<{ $variant?: "danger" }>`
  font-size: ${({ theme }) => theme.font.size.xs};
  padding: 3px 8px;
  border: 1px solid
    ${({ theme, $variant }) =>
      $variant === "danger" ? theme.color.error : theme.color.border};
  border-radius: ${({ theme }) => theme.radius.sm};
  background: none;
  color: ${({ theme, $variant }) =>
    $variant === "danger" ? theme.color.error : theme.color.textPrimary};
  cursor: pointer;

  &:hover {
    background: ${({ theme, $variant }) =>
      $variant === "danger"
        ? theme.color.errorMuted
        : theme.color.accentMuted};
    border-color: ${({ theme, $variant }) =>
      $variant === "danger"
        ? theme.color.error
        : theme.color.accent};
  }
`;

const ProgressBar = styled.div`
  height: 3px;
  background: ${({ theme }) => theme.color.border};
  border-radius: 2px;
  overflow: hidden;
  margin-top: ${({ theme }) => theme.space.xs};
`;

const ProgressFill = styled.div<{ $progress: number }>`
  width: ${({ $progress }) => $progress}%;
  height: 100%;
  background: ${({ theme }) => theme.color.accent};
  transition: width 50ms linear;
`;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatTime(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${String(s).padStart(2, "0")}`;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function TaskCard({
  task,
  index,
  doc,
  edit,
  playingId,
  progress,
  onPlay,
  onEdit,
}: {
  task: Task;
  index: number;
  doc: StructureDoc;
  edit: TaskEdit | undefined;
  playingId: string | null;
  progress: number;
  onPlay: (id: string, timestamp: number) => void;
  onEdit: (id: string, e: TaskEdit) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [editingText, setEditingText] = useState("");
  const [editingResponsible, setEditingResponsible] = useState("");
  const [editingDeadline, setEditingDeadline] = useState("");
  const [editingNotify, setEditingNotify] = useState(false);

  const isPlaying = playingId === task.id;
  const kind = edit?.kind;
  const displayText = edit?.text ?? task.text;
  const displayResponsible = edit?.responsibleName ?? task.responsibleName;
  const displayDeadline = edit?.deadline !== undefined
    ? edit.deadline
    : task.deadline;
  const displayNotify =
    edit?.requiresNotification !== undefined
      ? edit.requiresNotification
      : task.requiresNotification;

  const section = doc.sections.find((s) => s.id === task.sectionId);

  const startEdit = useCallback(() => {
    setEditingText(displayText);
    setEditingResponsible(displayResponsible);
    setEditingDeadline(displayDeadline ?? "");
    setEditingNotify(displayNotify);
    setEditing(true);
  }, [displayText, displayResponsible, displayDeadline, displayNotify]);

  const commitEdit = useCallback(() => {
    const newEdit: TaskEdit = { ...edit };

    if (editingText.trim() && editingText.trim() !== task.text) {
      newEdit.text = editingText.trim();
    } else if (editingText.trim() === task.text) {
      delete newEdit.text;
    }

    if (editingResponsible.trim() !== task.responsibleName) {
      newEdit.responsibleName = editingResponsible.trim();
    } else {
      delete newEdit.responsibleName;
    }

    const origDeadline = task.deadline ?? "";
    if (editingDeadline.trim() !== origDeadline) {
      newEdit.deadline = editingDeadline.trim() || "";
    } else {
      delete newEdit.deadline;
    }

    if (editingNotify !== task.requiresNotification) {
      newEdit.requiresNotification = editingNotify;
    } else {
      delete newEdit.requiresNotification;
    }

    onEdit(task.id, newEdit);
    setEditing(false);
  }, [
    editingText,
    editingResponsible,
    editingDeadline,
    editingNotify,
    task,
    edit,
    onEdit,
  ]);

  const cancelEdit = useCallback(() => {
    setEditingText(displayText);
    setEditingResponsible(displayResponsible);
    setEditingDeadline(displayDeadline ?? "");
    setEditingNotify(displayNotify);
    setEditing(false);
  }, [displayText, displayResponsible, displayDeadline, displayNotify]);

  const handleConfirm = useCallback(() => {
    if (kind === "confirm") {
      const rest = { ...edit };
      delete rest.kind;
      onEdit(task.id, rest);
    } else {
      onEdit(task.id, { ...edit, kind: "confirm" });
    }
  }, [kind, edit, task.id, onEdit]);

  const handleDismiss = useCallback(() => {
    if (kind === "dismiss") {
      const rest = { ...edit };
      delete rest.kind;
      onEdit(task.id, rest);
    } else {
      onEdit(task.id, { ...edit, kind: "dismiss" });
    }
  }, [kind, edit, task.id, onEdit]);

  const isConfirmed = kind === "confirm";
  const isDismissed = kind === "dismiss";

  return (
    <Card $confirmed={isConfirmed} $dismissed={isDismissed}>
      <TopRow>
        <PlayButton
          onClick={() => onPlay(task.id, task.timestamp)}
          title="Escuchar 10s desde esta tarea"
        >
          ▶
        </PlayButton>
        <Timestamp>{formatTime(task.timestamp)}</Timestamp>
        <Content>
          {editing ? (
            <>
              <InlineInput
                value={editingText}
                onChange={(e) => setEditingText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") commitEdit();
                  if (e.key === "Escape") cancelEdit();
                }}
                placeholder="Descripción de la tarea"
                autoFocus
              />
              <EditFields>
                <EditField>
                  Responsable:
                  <SmallInput
                    value={editingResponsible}
                    onChange={(e) =>
                      setEditingResponsible(e.target.value)
                    }
                    placeholder="Nombre"
                  />
                </EditField>
                <EditField>
                  Deadline:
                  <SmallInput
                    value={editingDeadline}
                    onChange={(e) =>
                      setEditingDeadline(e.target.value)
                    }
                    placeholder="ej. Viernes 15"
                  />
                </EditField>
                <ToggleButton
                  $active={editingNotify}
                  onClick={() => setEditingNotify((p) => !p)}
                >
                  Notificar: {editingNotify ? "Sí" : "No"}
                </ToggleButton>
              </EditFields>
            </>
          ) : (
            <>
              <TaskText $dismissed={isDismissed}>
                {displayText}
              </TaskText>
              {isConfirmed && (
                <div>
                  <StatusBadge $kind="confirm">
                    ✅ Confirmada
                  </StatusBadge>
                </div>
              )}
              {isDismissed && (
                <div>
                  <StatusBadge $kind="dismiss">
                    🗑 Eliminada
                  </StatusBadge>
                </div>
              )}
            </>
          )}
        </Content>
      </TopRow>

      <MetaRow>
        <MetaItem>
          <MetaLabel>#{index + 1}</MetaLabel>
        </MetaItem>
        <MetaItem>
          <MetaLabel>Responsable:</MetaLabel>{" "}
          {displayResponsible || "—"}
        </MetaItem>
        <MetaItem>
          <MetaLabel>Deadline:</MetaLabel>{" "}
          {displayDeadline || "—"}
        </MetaItem>
        <MetaItem>
          <MetaLabel>Sección:</MetaLabel> {section?.title ?? "—"}
        </MetaItem>
        <MetaItem>
          {displayNotify ? (
            <NotificationBadge>Notificar</NotificationBadge>
          ) : (
            "Sin notif."
          )}
        </MetaItem>
      </MetaRow>

      <Actions>
        {!isConfirmed && !isDismissed && (
          <>
            <ActionButton onClick={handleConfirm}>
              ✅ Confirmar
            </ActionButton>
            <ActionButton onClick={startEdit}>✏️ Editar</ActionButton>
            <ActionButton $variant="danger" onClick={handleDismiss}>
              🗑 Eliminar
            </ActionButton>
          </>
        )}
        {(isConfirmed || isDismissed) && (
          <ActionButton
            onClick={() => {
              const rest = { ...edit };
              delete rest.kind;
              onEdit(task.id, rest);
            }}
          >
            ↩ Revertir
          </ActionButton>
        )}
      </Actions>

      {isPlaying && (
        <ProgressBar>
          <ProgressFill $progress={progress} />
        </ProgressBar>
      )}
    </Card>
  );
}
